import React, { forwardRef, useEffect, useState } from 'react';
import ReactSelect, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import styled, { useTheme } from 'styled-components';

import { Option } from '../../../core';
import Button from '../Button';
import CheckboxInput from '../CheckboxInput';
import Text from '../Text';
import {
  AnimatedLabel,
  CheckboxWrapper,
  ErrorMessage,
  SelectInputLayout,
  StyledIcon,
  StyledLabel,
  StyledOptionContainer,
} from './SelectInput.styles';
import { SelectInputProps } from './SelectInput.types';
import inputStylesPrimary from './SelectInputPrimary.styles';
import inputStylesSecondary from './SelectInputSecondary.styles';
import inputStylesTertiary from './SelectInputTertiary.styles';

const getStylesByVariant = (variant: 'primary' | 'secondary' | 'tertiary') => {
  switch (variant) {
    case 'primary':
      return inputStylesPrimary;
    case 'secondary':
      return inputStylesSecondary;
    case 'tertiary':
      return inputStylesTertiary;
    default:
      return inputStylesPrimary;
  }
};

export const DropdownIndicator = (props: any) => {
  const theme = useTheme();
  if (props.selectProps.isError && props.selectProps.isErrorIcon) {
    return <ErrorIndicator {...props} />;
  }
  return (
    <components.DropdownIndicator {...props}>
      <StyledIcon
        type={theme.selectIndicatorIcon || 'caret-down'}
        size={theme.selectIndicatorIconSize || 20}
        viewSize={theme.selectIndicatorIconViewSize || 20}
      />
    </components.DropdownIndicator>
  );
};

export const SearchIndicator = (props: any) => {
  const theme = useTheme();
  if (props.selectProps.isError && props.selectProps.isErrorIcon) {
    return <ErrorIndicator {...props} />;
  }
  return (
    !props.hasValue && (
      <components.DropdownIndicator {...props}>
        <StyledIcon
          type={theme.selectIndicatorSearchIcon || 'search'}
          size={theme.selectIndicatorSeachIconSize || 20}
          viewSize={theme.selectIndicatorSeachIconViewSize || 20}
        />
      </components.DropdownIndicator>
    )
  );
};

export const ClearIndicator = (props: any) => {
  return (
    <components.ClearIndicator {...props}>
      <StyledIcon type="remove" />
    </components.ClearIndicator>
  );
};
export const ErrorIndicator = (props: any) => {
  const theme = useTheme();

  return (
    <components.DropdownIndicator {...props}>
      <StyledIcon type="danger" color={theme.errorColor} />
    </components.DropdownIndicator>
  );
};

export const InputOption = (props: any) => {
  return (
    <components.Option {...props}>
      <CheckboxWrapper>
        <CheckboxInput
          id={props.value}
          checked={props.isSelected}
          label={props.label}
          name={props.value}
          onChange={() => null}
          disableClick={true}
        />
      </CheckboxWrapper>
    </components.Option>
  );
};

export const MultiValue = (props: any) => {
  return (
    <components.MultiValue {...props}>
      <Text noMargin size="xsmall">
        {props.data.label}
      </Text>
    </components.MultiValue>
  );
};

export const Control = (props: any) => {
  return (
    <React.Fragment>
      <components.Control {...props} />
      {props.selectProps.animatedLabel && (
        <AnimatedLabel>
          {props.selectProps.animatedLabel}
          {props.selectProps.showRequired && '*'}
        </AnimatedLabel>
      )}
    </React.Fragment>
  );
};

export const NoOptionsMessage = (props: any) => {
  return (
    <React.Fragment>
      {props.selectProps.inputValue && (
        <components.NoOptionsMessage {...props}>
          <Text noMargin>No Results Found</Text>
        </components.NoOptionsMessage>
      )}
    </React.Fragment>
  );
};

export const AsyncOption = (props: any) => {
  if (!!props.selectProps.secondaryOption) {
    return (
      <StyledOptionContainer isFocused={props.isFocused}>
        <components.Option {...props} />

        <Text noMargin size="medium" weight="700">
          {props.data.label}
        </Text>

        <Button
          variant="secondary"
          size="xsmall"
          onClick={() => props.selectProps.secondaryOption.handleOnclick(props.data)}
        >
          {props.selectProps.secondaryOption.label}
        </Button>
      </StyledOptionContainer>
    );
  }

  return <components.Option {...props} />;
};

const StyledReactSelect = styled(ReactSelect).attrs({
  classNamePrefix: 'react-select',
  components: { DropdownIndicator, Control },
})<SelectInputProps>`
  ${({ variant }) => variant && getStylesByVariant(variant)}
`;

const StyledAsyncSelect = styled(AsyncSelect).attrs({
  classNamePrefix: 'react-select',
  components: {
    DropdownIndicator: SearchIndicator,
    ClearIndicator,
    NoOptionsMessage,
    Option: AsyncOption,
  },
})<SelectInputProps>`
  ${({ variant }) => variant && getStylesByVariant(variant)}
`;

const StyledAsyncMultiSelect = styled(AsyncSelect).attrs({
  classNamePrefix: 'react-select',
  components: {
    DropdownIndicator: SearchIndicator,
    ClearIndicator,
    Option: InputOption,
    MultiValue,
    NoOptionsMessage,
  },
})<SelectInputProps>`
  ${({ variant }) => variant && getStylesByVariant(variant)}
`;

const StyledMultiSelect = styled(ReactSelect).attrs({
  classNamePrefix: 'react-select',
  components: {
    DropdownIndicator,
    ClearIndicator,
    Option: InputOption,
    MultiValue,
    NoOptionsMessage,
  },
})<SelectInputProps>`
  ${({ variant }) => variant && getStylesByVariant(variant)}
`;

const SelectInput = forwardRef(
  (
    {
      className,
      disabled,
      width,
      label,
      animatedLabel,
      labelType = 'outside',
      onChange,
      isAsync = false,
      showRequired,
      errorMessage,
      isErrorIcon = false,
      variant = 'primary',
      isSearchable = true,
      menuPosition = 'absolute',
      dataFor,
      dataTip,
      ...inputProps
    }: SelectInputProps,
    ref: any
  ) => {
    const [inputValue, setInputValue] = useState(inputProps.defaultValue || inputProps.value);
    const [hasEnteredText, setHasEnteredText] = useState(false);

    useEffect(() => {
      checkUserInput();
    }, [inputValue]);

    const checkUserInput = () => {
      if (inputValue) {
        setHasEnteredText(true);
      } else {
        setHasEnteredText(false);
      }
    };

    const renderInput = () => {
      if (isAsync) {
        if (inputProps.isMulti) {
          return (
            <StyledAsyncMultiSelect
              ref={ref}
              isDisabled={disabled}
              isError={!!errorMessage}
              onChange={(val: Option) => {
                setInputValue(val);
                onChange && onChange(val);
              }}
              labelType={labelType}
              variant={variant}
              isErrorIcon={isErrorIcon}
              {...inputProps}
            />
          );
        }

        return (
          <StyledAsyncSelect
            ref={ref}
            isDisabled={disabled}
            isError={!!errorMessage}
            onChange={(val: Option) => {
              setInputValue(val);
              onChange && onChange(val);
            }}
            labelType={labelType}
            variant={variant}
            isErrorIcon={isErrorIcon}
            {...inputProps}
          />
        );
      } else {
        if (inputProps.isMulti) {
          return (
            <StyledMultiSelect
              ref={ref}
              isDisabled={disabled}
              isError={!!errorMessage}
              onChange={(val: Option) => {
                setInputValue(val);
                onChange && onChange(val);
              }}
              labelType={labelType}
              isErrorIcon={isErrorIcon}
              variant={variant}
              {...inputProps}
            />
          );
        }

        return (
          <StyledReactSelect
            ref={ref}
            isDisabled={disabled}
            isError={!!errorMessage}
            onChange={(val: Option) => {
              setInputValue(val);
              onChange && onChange(val);
            }}
            labelType={labelType}
            variant={variant}
            animatedLabel={animatedLabel}
            showRequired={showRequired}
            isSearchable={isSearchable}
            isErrorIcon={isErrorIcon}
            menuPlacement="auto"
            menuPosition={menuPosition}
            {...inputProps}
          />
        );
      }
    };

    return (
      <SelectInputLayout className={className} width={width} hasEnteredText={hasEnteredText}>
        {label && (
          <StyledLabel labelType={labelType} variant={variant}>
            {label}
            {showRequired && '*'}
          </StyledLabel>
        )}
        {dataFor && (dataTip || dataTip === '') ? (
          <span data-for={dataFor} data-tip={dataTip}>
            {renderInput()}
          </span>
        ) : (
          renderInput()
        )}
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </SelectInputLayout>
    );
  }
);

export default SelectInput;
